import Big from "big.js";
import type { TransactionChangeRow } from "../api/specialized.ts";

/**
 * Port of WebAdmin2's `WorkingDataRental.Calc` + `ReCalc`
 * (popupEditCart2.ts:145-258), faithful including quirks.
 *
 * For RowType 1 (rental) / 45 (reserve) rows that carry a `RentalInfo` block,
 * the late fee is COMPUTED from return-vs-due dates + the row's RentalInfo —
 * the client is authoritative (the server persists what's sent). When
 * `RentalInfo` is absent (non-rental DBs), this returns a passthrough that
 * mirrors today's manual-input behaviour.
 *
 * Money math uses Big.js (no extra rounding — WebAdmin2 keeps full Big
 * precision; display formats with toFixed(2) elsewhere).
 */
export interface RentalRecalc {
    applicable: boolean;       // false when RentalInfo missing
    isReserve: boolean;        // RowType === 45
    latePeriods: number;
    rentalPrice: number; rentalTax: number;   // RentalFinal*
    latePrice: number;   lateTax: number;     // LateFinal*
    buyPrice: number;    buyTax: number;      // BuyFinal*
    sold: boolean;
    finalPrice: number;  finalTax: number;    // FinalPrice/Tax (incl buy when sold)
}

export function computeRentalRecalc(row: TransactionChangeRow): RentalRecalc {
    const isReserve = row.RowType === 45;
    const ri = row.RentalInfo;

    // Non-applicable fallback: passthrough of today's manual fields.
    if (ri == null) {
        const initPrice = row.InitialAmount ?? 0;
        const initTax = row.InitialTax ?? 0;
        const latePrice = row.LateAmount ?? 0;
        const lateTax = row.LateTax ?? 0;
        return {
            applicable: false,
            isReserve,
            latePeriods: 0,
            rentalPrice: initPrice, rentalTax: initTax,
            latePrice, lateTax,
            buyPrice: 0, buyTax: 0,
            sold: false,
            finalPrice: initPrice + latePrice,
            finalTax: initTax + lateTax,
        };
    }

    // ── Calc: seed bases + compute late periods ──────────────
    const rentalBasePrice = new Big(row.InitialAmount ?? 0);
    const rentalBaseTax = new Big(row.InitialTax ?? 0);
    const lateBasePrice = new Big(ri.LatePeriodPrice ?? 0);
    const lateBaseTax = new Big(ri.LateTaxRate ?? 0);
    const buyBasePrice = new Big(ri.MaxPrice ?? 0);
    const buyBaseTax = new Big(ri.MaxTax ?? 0);

    const dueBy = new Date(row.DueByDate as string);
    const returnDate = row.ReturnDate ? new Date(row.ReturnDate) : new Date();
    // WebAdmin2 mutates dueBy to the biased time and reuses it for minutesLate.
    const biasedDueMs = dueBy.getTime() + (row.Bias ?? 0) * 60 * 1000;

    let latePeriods: number;
    const rentalPeriod = ri.RentalPeriod;
    if (returnDate.getTime() <= biasedDueMs) {
        latePeriods = 0;
    } else if (!rentalPeriod || rentalPeriod <= 0) {
        // Guard against divide-by-zero; WebAdmin2 assumes RentalPeriod is set.
        latePeriods = 0;
    } else {
        // NOTE: uses the BIASED due (parity with WebAdmin2's mutated dueBy).
        const minutesLate = (returnDate.getTime() - biasedDueMs) / 60000;
        latePeriods = row.RowType === 1
            ? Math.ceil(Math.ceil(minutesLate / 60) * 10000.0 / rentalPeriod) / 10000.0
            : Math.ceil(minutesLate / rentalPeriod);
    }

    // ── ReCalc: derive final money figures ───────────────────
    let rentalFinalPrice: Big, rentalFinalTax: Big;
    let lateFinalPrice: Big, lateFinalTax: Big;
    let buyFinalPrice: Big, buyFinalTax: Big;
    let finalPrice: Big, finalTax: Big;
    let sold: boolean;

    const out = (): RentalRecalc => ({
        applicable: true,
        isReserve,
        latePeriods,
        rentalPrice: rentalFinalPrice.toNumber(), rentalTax: rentalFinalTax.toNumber(),
        latePrice: lateFinalPrice.toNumber(), lateTax: lateFinalTax.toNumber(),
        buyPrice: buyFinalPrice.toNumber(), buyTax: buyFinalTax.toNumber(),
        sold,
        finalPrice: finalPrice.toNumber(), finalTax: finalTax.toNumber(),
    });

    if (latePeriods === 0) {
        rentalFinalPrice = rentalBasePrice;
        rentalFinalTax = rentalBaseTax;
        lateFinalPrice = new Big(0);
        lateFinalTax = new Big(0);
        buyFinalPrice = new Big(0);
        buyFinalTax = new Big(0);
        finalPrice = rentalBasePrice;
        finalTax = rentalBaseTax;
        sold = false;
        return out();
    }

    const latePrice = lateBasePrice.times(latePeriods);
    const lateTax = row.RowType === 1
        ? lateBasePrice.times(lateBaseTax)
        : lateBaseTax.times(0.01).times(latePrice);
    const totalRentCheck = rentalBasePrice.plus(rentalBaseTax).plus(latePrice).plus(lateTax)
        .times(row.RowType === 45 ? 1 : 2);
    const totalBuy = buyBasePrice.plus(buyBaseTax);

    if (totalRentCheck.gt(totalBuy) && !totalBuy.eq(0)) {
        lateFinalPrice = new Big(0);
        lateFinalTax = new Big(0);
        if (!ri.ChargeSold) {
            rentalFinalPrice = rentalBasePrice;
            rentalFinalTax = rentalBaseTax;
            buyFinalPrice = new Big(0);
            buyFinalTax = new Big(0);
            finalPrice = rentalFinalPrice;
            finalTax = rentalFinalTax;
            sold = false;
            return out();
        }
        rentalFinalPrice = new Big(0);
        rentalFinalTax = new Big(0);
        buyFinalPrice = buyBasePrice;
        buyFinalTax = buyBaseTax;
        finalPrice = buyFinalPrice;
        finalTax = buyFinalTax;
        sold = true;
        return out();
    }

    sold = false;
    buyFinalPrice = new Big(0);
    buyFinalTax = new Big(0);
    rentalFinalPrice = rentalBasePrice;
    rentalFinalTax = rentalBaseTax;
    if (!ri.ChargeLate) {
        lateFinalPrice = new Big(0);
        lateFinalTax = new Big(0);
        finalPrice = rentalFinalPrice;
        finalTax = rentalFinalTax;
        return out();
    }
    lateFinalPrice = latePrice;
    lateFinalTax = lateTax;
    finalPrice = lateFinalPrice.plus(rentalFinalPrice);
    finalTax = lateFinalTax.plus(rentalFinalTax);
    return out();
}
